import { useState, useCallback, useEffect, useRef, type ReactNode } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CopyId, EmptyState, StatusBadge } from '@pazportal/ui';
import { estimatorEstimateUrl } from '../lib/estimatorUrl';
import { portalViewAsUrl } from '../lib/portalUrl';
import { invokeTriggerTask, describeJobError } from '../lib/triggerTask';
import { useCustomerDetail } from '../hooks/useCustomerDetail';
import { useAllCustomersWithSignals, HEALTH_STATUS_LABEL } from '../hooks/useAllCustomersWithSignals';
import { HealthPill, HealthProvenance, SignalList } from '../components/HealthSignals';
import { supabase } from '../lib/supabase';
import {
  formatCurrency,
  formatSlaTier,
  formatSolutionType,
  formatAuditAction,
  formatDate,
  formatDateTime,
  timeAgo,
  daysUntil,
} from '../lib/formatters';
import { useAuth } from '../contexts/AuthContext';
import { canManageCustomerAccess } from '../lib/customerAccess';
import {
  useAuvikTenants,
  useAuvikTenantsForCustomer,
  useMapAuvikTenant,
  useUnmapAuvikTenant,
  useLocationsForCustomer,
} from '../hooks/useAuvikTenants';
import {
  useContactsForCustomer,
  useCustomerUsers,
  useInviteCustomerUser,
  type ContactLite,
  type CustomerUserRow,
} from '../hooks/useCustomerProvisioning';
import { useStaffUsers } from '../hooks/useStaffUsers';
import { formatAvailabilityPct, useCustomerNetworkLive } from '../hooks/useCustomerNetworkLive';

type Tab = 'locations' | 'deployments' | 'tickets' | 'contracts' | 'contacts' | 'access' | 'activity' | 'auvik';

type NotesState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

interface InvitePrefill {
  email: string;
  full_name: string;
  contact_id?: string;
  zoho_contact_id?: string;
  is_primary_contact?: boolean;
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { role } = useAuth();
  const canManageAccess = canManageCustomerAccess(role);
  const { data, isLoading, error } = useCustomerDetail(id);
  const { data: roster, isLoading: rosterLoading } = useAllCustomersWithSignals();
  const { data: contacts } = useContactsForCustomer(id ?? null);
  const { data: portalUsers } = useCustomerUsers(id ?? null);
  const { data: mappedTenants } = useAuvikTenantsForCustomer(id ?? null);
  const { data: live } = useCustomerNetworkLive(id);
  const [tab, setTab] = useState<Tab>('locations');
  const [notes, setNotes] = useState<string | null>(null);
  const [notesState, setNotesState] = useState<NotesState>('idle');
  const [notesError, setNotesError] = useState<string | null>(null);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState<InvitePrefill | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const customer = data?.customer;
  const rosterRow = roster?.find((c) => c.id === id) ?? null;

  const saveNotes = useCallback(async () => {
    if (!id || notes === null) return;
    if (notes === (customer?.internal_notes ?? '')) {
      setNotesState('idle');
      return;
    }
    setNotesState('saving');
    setNotesError(null);
    const { error: updateError } = await supabase.from('customers').update({ internal_notes: notes }).eq('id', id);
    if (updateError) {
      setNotesState('error');
      setNotesError(updateError.message);
      return;
    }
    setNotesState('saved');
    await qc.invalidateQueries({ queryKey: ['admin-customer-detail', id] });
  }, [id, notes, customer?.internal_notes, qc]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 4000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" aria-hidden="true" />
        <span className="sr-only">Loading customer</span>
      </div>
    );
  }

  if (error || !customer || !id) {
    return (
      <div className="p-6 text-center">
        <p className="text-notification" role="alert">Customer not found.</p>
        <button type="button" onClick={() => navigate('/customers')} className="mt-2 text-sm text-primary hover:underline min-h-[44px]">Back to roster</button>
      </div>
    );
  }

  const renewalDays = daysUntil(customer.contract_renewal_date);
  const openTickets = data.tickets?.length ?? 0;
  const auvikConnected = (mappedTenants?.length ?? 0) > 0;
  const openAlerts = live?.alerts ?? [];
  const primaryContact = (contacts ?? []).find((c) => c.is_primary && c.email) ?? (contacts ?? []).find((c) => c.email) ?? null;
  const provisionedContactIds = new Set((portalUsers ?? []).map((u) => u.contact_id).filter((v): v is string => Boolean(v)));

  const tabs: { key: Tab; label: string }[] = [
    { key: 'locations', label: `Locations (${data.locations.length})` },
    { key: 'deployments', label: `Deployments (${data.deployments.length})` },
    { key: 'tickets', label: `Tickets (${openTickets})` },
    { key: 'contracts', label: 'Contract' },
    { key: 'contacts', label: `Zoho contacts (${contacts?.length ?? 0})` },
    { key: 'access', label: `Portal users (${portalUsers?.length ?? 0})` },
    { key: 'activity', label: 'Activity' },
    { key: 'auvik', label: 'Auvik' },
  ];

  return (
    <div className="space-y-4">
      <button type="button" onClick={() => navigate('/customers')} className="text-sm text-primary hover:underline min-h-[44px] sm:min-h-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded">&larr; Back to customers</button>

      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-text break-words">{customer.name}</h1>
          <p className="text-xs text-text-muted mt-0.5">{customer.account_number} · <StatusBadge status={customer.status ?? 'unknown'} /></p>
        </div>
        {rosterRow && <HealthPill health={rosterRow.health} className="self-start" />}
      </div>

      {/* Three-panel grid */}
      <div className="grid gap-4 lg:gap-6 grid-cols-1 lg:grid-cols-[240px_1fr_200px]">
        {/* LEFT PANEL */}
        <div className="space-y-5">
          <section aria-labelledby="health-heading" className="space-y-2">
            <h2 id="health-heading" className="text-xs font-semibold text-text-muted uppercase tracking-wider">Health</h2>
            {rosterRow ? (
              <>
                <div className="flex items-center gap-2">
                  <HealthPill health={rosterRow.health} />
                </div>
                <SignalList health={rosterRow.health} />
                <HealthProvenance health={rosterRow.health} />
              </>
            ) : rosterLoading ? (
              <p className="text-xs text-text-muted">Loading health signals…</p>
            ) : (
              <p className="text-xs text-text-muted">{HEALTH_STATUS_LABEL.unknown}: this customer is not in the roster yet.</p>
            )}
          </section>

          <section aria-labelledby="account-heading" className="space-y-1.5">
            <h2 id="account-heading" className="text-xs font-semibold text-text-muted uppercase tracking-wider">Account</h2>
            <Fact label="MRR" value={formatCurrency(customer.mrr_cents)} />
            <Fact label="SLA tier" value={formatSlaTier(customer.sla_tier)} />
            <Fact label="Locations" value={String(data.locations.length)} />
            <Fact label="Deployments" value={String(data.deployments.length)} />
            <Fact label="Renewal" value={renewalDays !== null ? `${renewalDays} days` : 'No date'} />
            <Fact label="Zoho" value={customer.zoho_account_id ? 'Linked' : 'Not linked'} />
          </section>

          <section aria-labelledby="notes-heading">
            <div className="flex items-center justify-between mb-1">
              <h2 id="notes-heading" className="text-xs font-semibold text-text-muted uppercase tracking-wider">Internal notes</h2>
              <span className="text-[11px]" aria-live="polite">
                {notesState === 'saving' && <span className="text-text-muted">Saving…</span>}
                {notesState === 'saved' && <span className="text-success">Saved</span>}
                {notesState === 'dirty' && <span className="text-text-faint">Unsaved</span>}
                {notesState === 'error' && <span className="text-error">Not saved</span>}
              </span>
            </div>
            <label className="sr-only" htmlFor="internal-notes">Internal notes</label>
            <textarea
              id="internal-notes"
              value={notes ?? customer.internal_notes ?? ''}
              onChange={(e) => { setNotes(e.target.value); setNotesState('dirty'); }}
              onBlur={() => void saveNotes()}
              rows={4}
              className="w-full text-xs px-2 py-1.5 border border-border rounded-md bg-surface text-text resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Staff-only notes. Never shown to the customer."
            />
            {notesState === 'error' && (
              <p role="alert" className="text-[11px] text-error mt-1 break-words">
                Could not save notes{notesError ? `: ${notesError}` : ''}. Edit and leave the field to retry.
              </p>
            )}
          </section>
        </div>

        {/* CENTER PANEL */}
        <div className="min-w-0">
          <div className="flex gap-1 border-b border-border mb-4 overflow-x-auto -mx-1 px-1" role="tablist" aria-label="Customer sections">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-2 min-h-[44px] text-sm font-medium border-b-2 whitespace-nowrap transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-t ${
                  tab === t.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-text-muted hover:text-text'
                }`}
              >{t.label}</button>
            ))}
          </div>

          <div role="tabpanel">
            {tab === 'locations' && <LocationsTab locations={data.locations as unknown as Array<Record<string, unknown>>} />}
            {tab === 'deployments' && (
              <DeploymentsTab
                deployments={data.deployments as unknown as Array<Record<string, unknown>>}
                trips={data.trips}
              />
            )}
            {tab === 'tickets' && <TicketsTab tickets={data.tickets as unknown as Array<Record<string, unknown>>} />}
            {tab === 'contracts' && <ContractsTab customer={customer as unknown as Record<string, unknown>} />}
            {tab === 'contacts' && (
              <ContactsTab
                contacts={contacts ?? []}
                canManage={canManageAccess}
                provisionedContactIds={provisionedContactIds}
                onInvite={(c) =>
                  setInvitePrefill({
                    email: c.email ?? '',
                    full_name: c.full_name ?? '',
                    contact_id: c.id,
                    zoho_contact_id: c.zoho_contact_id ?? undefined,
                    is_primary_contact: c.is_primary,
                  })
                }
              />
            )}
            {tab === 'access' && (
              <PortalUsersPanel canManage={canManageAccess} users={portalUsers ?? []} onInvite={() => setInvitePrefill({ email: '', full_name: '' })} />
            )}
            {tab === 'activity' && <ActivityTab entries={data.auditLog as unknown as Array<Record<string, unknown>>} isSuperadmin={role === 'superadmin'} />}
            {tab === 'auvik' && <AuvikTab customerId={id} />}
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Quick actions</p>
          <ActionBtn label="Create ticket" onClick={() => setShowTicketDialog(true)} />
          {canManageAccess && <ActionBtn label="Invite to portal" onClick={() => setInvitePrefill({ email: '', full_name: '' })} />}
          <ActionBtn label="Invoices" onClick={() => navigate('/invoices')} />
          <ActionBtn label="Field trips" onClick={() => navigate('/trips')} />
          <ActionBtn label="Network alerts" onClick={() => navigate('/command-center')} />
          <ActionBtn
            label={primaryContact ? `Email ${primaryContact.full_name ?? primaryContact.email}` : 'Email primary contact'}
            onClick={() => {
              if (primaryContact?.email) window.open(`mailto:${primaryContact.email}`);
              else showToast('No Zoho contact with an email address is synced for this customer.');
            }}
          />
          <GenerateSlaReportButton customerId={id} />

          <div className="mt-4 space-y-1.5">
            <p className="text-xs font-semibold text-text-muted uppercase tracking-wider">Recent alerts</p>
            {!auvikConnected ? (
              <p className="text-[11px] text-text-muted leading-snug">
                Auvik not connected: no Auvik tenant is mapped to this customer.{' '}
                <button type="button" onClick={() => setTab('auvik')} className="text-primary hover:underline">Map a tenant</button>
              </p>
            ) : openAlerts.length === 0 ? (
              <p className="text-[11px] text-text-muted leading-snug">No open Auvik alerts.</p>
            ) : (
              <ul className="space-y-1.5">
                {openAlerts.slice(0, 3).map((a) => (
                  <li key={a.id} className="flex items-start gap-1.5">
                    <span className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${severityDot(a.severity)}`} aria-hidden="true" />
                    <span className="text-[10px] text-text-muted leading-tight break-words">
                      {a.alert_name ?? a.entity_name ?? 'Alert'}
                      {a.detected_on ? ` — ${timeAgo(a.detected_on)}` : ''}
                    </span>
                  </li>
                ))}
                {openAlerts.length > 3 && (
                  <li>
                    <Link to="/command-center" className="text-[10px] text-primary hover:underline">
                      {openAlerts.length - 3} more on Command Center
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Dialogs */}
      {showTicketDialog && (
        <TicketDialog
          customerId={id}
          customerName={customer.name}
          locations={data.locations as unknown as Array<Record<string, unknown>>}
          onClose={() => setShowTicketDialog(false)}
          onCreated={() => showToast('Ticket created')}
        />
      )}
      {invitePrefill && canManageAccess && (
        <InviteDialog
          customerId={id}
          customerName={customer.name}
          prefill={invitePrefill}
          onClose={() => setInvitePrefill(null)}
        />
      )}

      {toast && (
        <div role="status" className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] right-4 left-4 sm:left-auto sm:right-6 z-50 px-4 py-2.5 bg-surface-strong border border-border text-text text-sm rounded-lg shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}

function severityDot(severity: string | null): string {
  const s = (severity ?? '').toLowerCase();
  if (s === 'critical' || s === 'emergency') return 'bg-error';
  if (s === 'warning' || s === 'high') return 'bg-warning';
  return 'bg-info';
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-2 text-xs">
      <span className="text-text-muted">{label}</span>
      <span className="font-medium text-text text-right">{value}</span>
    </div>
  );
}

function ActionBtn({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full min-h-[44px] lg:min-h-[36px] text-xs font-medium rounded-md border border-border bg-surface text-text hover:bg-surface-strong transition-colors disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >{label}</button>
  );
}

/* ─── Tab panels ─── */

function LocationsTab({ locations }: { locations: Array<Record<string, unknown>> }) {
  return (
    <div className="space-y-2">
      {locations.length === 0 && (
        <EmptyState
          icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /></svg>}
          title="No locations"
          subtitle="Locations are created during onboarding or by the Zoho sync."
        />
      )}
      {locations.map((loc: Record<string, unknown>) => {
        const deps = (loc.deployments as Array<{ id: string; solution_type: string; status: string }>) ?? [];
        const hierarchy = loc.hierarchy as { property_code?: string } | null;
        return (
          <Link key={loc.id as string} to={`/locations/${loc.id as string}`} className="block p-3 rounded-md border border-border bg-surface-2 hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-text truncate">{loc.name as string}</p>
              <StatusBadge status={(loc.status as string) ?? 'unknown'} />
            </div>
            <p className="text-xs text-text-muted mt-0.5">
              {deps.length} deployment{deps.length === 1 ? '' : 's'}
              {hierarchy?.property_code ? ` · property ${hierarchy.property_code}` : ' · no property code'}
            </p>
          </Link>
        );
      })}
    </div>
  );
}

function DeploymentsTab({
  deployments,
  trips,
}: {
  deployments: Array<Record<string, unknown>>;
  trips: Array<Record<string, unknown>>;
}) {
  const grouped = new Map<string, Array<Record<string, unknown>>>();
  for (const d of deployments) {
    const type = (d.solution_type as string) ?? 'unknown';
    if (!grouped.has(type)) grouped.set(type, []);
    grouped.get(type)!.push(d);
  }

  return (
    <div className="space-y-4">
      {deployments.length === 0 && (
        <EmptyState
          icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>}
          title="No deployments recorded"
          subtitle="Accepted estimates and onboarding create deployment rows."
        />
      )}
      {Array.from(grouped.entries()).map(([type, deps]) => (
        <div key={type}>
          <h3 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">{formatSolutionType(type)} ({deps.length})</h3>
          <div className="space-y-1.5">
            {deps.map((d) => (
              <DeploymentRow
                key={d.id as string}
                deployment={d}
                trip={trips.find((t) => t.deployment_id === d.id) ?? null}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DeploymentRow({
  deployment,
  trip,
}: {
  deployment: Record<string, unknown>;
  trip: Record<string, unknown> | null;
}) {
  const [showScheduler, setShowScheduler] = useState(false);
  const status = deployment.status as string;
  const installing = status === 'installing';
  const estimateId = deployment.estimate_id as string | null;

  return (
    <div className="rounded border border-border bg-surface-2">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-2">
        <div className="min-w-0">
          <p className="text-sm text-text truncate">
            {((deployment.locations as Record<string, unknown>)?.name as string) ?? 'Unknown location'}
          </p>
          <p className="text-xs text-text-faint">
            {(deployment.vendor as string) ?? 'No vendor'}
            {estimateId ? (
              <>
                {' · '}
                <a href={estimatorEstimateUrl(estimateId)} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  Open estimate
                </a>
              </>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={status ?? 'unknown'} />
          {trip && (
            <Link
              to={`/trips?trip=${trip.id as string}`}
              className="text-xs text-primary hover:underline"
            >
              {(trip.project_number as string) ?? 'View trip'}
            </Link>
          )}
          {installing && !trip && !showScheduler && (
            <button
              type="button"
              onClick={() => setShowScheduler(true)}
              className="text-xs rounded bg-primary hover:bg-primary-hover px-2 py-1 min-h-[36px] text-text-inverse focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              Schedule trip
            </button>
          )}
        </div>
      </div>
      {showScheduler && (
        <FieldTripScheduler
          deploymentId={deployment.id as string}
          onClose={() => setShowScheduler(false)}
        />
      )}
    </div>
  );
}

function FieldTripScheduler({
  deploymentId,
  onClose,
}: {
  deploymentId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: staff } = useStaffUsers();
  const today = new Date().toISOString().slice(0, 10);
  const inAWeek = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(inAWeek);
  const [scope, setScope] = useState('');
  const [leadTechnicianId, setLeadTechnicianId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [tripId, setTripId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      const { data, error: rpcError } = await (
        supabase as unknown as {
          rpc: (
            fn: string,
            args: Record<string, unknown>,
          ) => Promise<{ data: string | null; error: { message: string } | null }>;
        }
      ).rpc('schedule_deployment_field_trip', {
        p_deployment_id: deploymentId,
        p_start_date: startDate,
        p_end_date: endDate,
        p_scope: scope || null,
        p_lead_technician_id: leadTechnicianId || null,
      });
      if (rpcError) throw new Error(rpcError.message);
      setTripId(data);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin-customer-detail'] }),
        qc.invalidateQueries({ queryKey: ['admin-field-trips'] }),
      ]);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const input = 'rounded border border-border bg-surface px-2 py-1 min-h-[40px] text-text focus:outline-none focus:ring-2 focus:ring-primary';

  return (
    <div className="border-t border-border px-3 py-2 space-y-2 text-xs">
      <div className="grid grid-cols-2 gap-2">
        <label className="flex flex-col gap-0.5">
          <span className="text-text-muted">Start</span>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={input} />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-text-muted">End</span>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={input} />
        </label>
      </div>
      <label className="flex flex-col gap-0.5">
        <span className="text-text-muted">Lead technician (optional)</span>
        <select value={leadTechnicianId} onChange={(e) => setLeadTechnicianId(e.target.value)} className={input}>
          <option value="">Unassigned</option>
          {(staff ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.full_name ?? u.email}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-0.5">
        <span className="text-text-muted">Scope (optional)</span>
        <input type="text" value={scope} onChange={(e) => setScope(e.target.value)} className={`w-full ${input}`} />
      </label>
      {error && <p role="alert" className="text-notification break-words">Could not schedule this trip: {error}</p>}
      {tripId && (
        <p role="status" className="text-success">
          Trip created.{' '}
          <Link to={`/trips?trip=${tripId}`} className="underline">Open field trips</Link>
        </p>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded px-2 py-1 min-h-[36px] text-text-muted hover:text-text">Close</button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting || !!tripId}
          className="rounded bg-primary hover:bg-primary-hover px-2 py-1 min-h-[36px] text-text-inverse disabled:opacity-60"
        >
          {submitting ? 'Scheduling…' : tripId ? 'Done' : 'Schedule'}
        </button>
      </div>
    </div>
  );
}

function TicketsTab({ tickets }: { tickets: Array<Record<string, unknown>> }) {
  return (
    <div className="space-y-2">
      {tickets.length === 0 && (
        <EmptyState
          icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
          title="No open tickets"
          subtitle="Create a ticket from Quick actions."
        />
      )}
      {tickets.map((t) => (
        <Link key={t.id as string} to={`/tickets/${t.id as string}`} className="block p-3 rounded-md border border-border bg-surface-2 hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div className="min-w-0">
              <span className="text-xs font-mono text-text-faint">{t.ticket_number as string}</span>
              <p className="text-sm font-medium text-text truncate">{t.title as string}</p>
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={(t.priority as string) ?? 'medium'} />
              <StatusBadge status={(t.status as string) ?? 'open'} />
            </div>
          </div>
          <p className="text-xs text-text-muted mt-1">
            {((t.location as Record<string, unknown>)?.name as string) ?? 'No site'} — Assigned: {((t.assigned_to as Record<string, unknown>)?.full_name as string) ?? 'Unassigned'}
          </p>
        </Link>
      ))}
    </div>
  );
}

function ContractsTab({ customer }: { customer: Record<string, unknown> }) {
  const renewalDays = daysUntil(customer.contract_renewal_date as string | null);
  const cap = (v: unknown) => (typeof v === 'string' && v ? v.charAt(0).toUpperCase() + v.slice(1) : '—');
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <InfoCard label="SLA tier" value={formatSlaTier(customer.sla_tier as string)} />
        <InfoCard label="Billing cycle" value={cap(customer.billing_cycle)} />
        <InfoCard label="MRR" value={formatCurrency(customer.mrr_cents as number)} />
        <InfoCard label="ARR (MRR × 12)" value={formatCurrency((customer.mrr_cents as number) * 12)} />
        <InfoCard label="Renewal date" value={formatDate(customer.contract_renewal_date as string | null)} />
        <InfoCard label="Days to renewal" value={renewalDays !== null ? `${renewalDays} days` : 'No renewal date'} />
        <InfoCard label="Status" value={cap(customer.status)} />
      </div>
    </div>
  );
}

function ActivityTab({ entries, isSuperadmin }: { entries: Array<Record<string, unknown>>; isSuperadmin: boolean }) {
  if (!isSuperadmin) {
    return <p className="text-sm text-text-muted">The audit log is readable by superadmins only.</p>;
  }
  return (
    <div className="space-y-2">
      {entries.length === 0 && <p className="text-sm text-text-muted">No audit entries for this customer record yet.</p>}
      {entries.map((e) => (
        <div key={e.id as string} className="flex items-start gap-3 p-2">
          <span className="text-xs font-mono text-text-faint shrink-0 w-20">{timeAgo(e.created_at as string)}</span>
          <div>
            <span className="text-sm text-text font-medium">{e.actor_label as string}</span>
            <span className="text-sm text-text-muted"> {formatAuditAction(e.action as string)}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

function ContactsTab({
  contacts,
  provisionedContactIds,
  canManage,
  onInvite,
}: {
  contacts: ContactLite[];
  provisionedContactIds: Set<string>;
  canManage: boolean;
  onInvite: (contact: ContactLite) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="text-xs text-text-muted">Zoho CRM is the master for contacts. Edit them in Zoho; invite them to the portal from here.</p>
      {contacts.length === 0 && (
        <EmptyState
          icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
          title="No Zoho contacts synced"
          subtitle="Run a Contacts pull on the Zoho Sync page. You can still invite a portal user by email from Quick actions."
        />
      )}
      {contacts.map((c) => {
        const provisioned = provisionedContactIds.has(c.id);
        return (
          <div key={c.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 rounded-md border border-border bg-surface-2">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text truncate">
                {c.full_name ?? c.email ?? 'Unnamed contact'}
                {c.is_primary && <span className="ml-2 rounded bg-primary-soft px-1.5 py-0.5 text-[10px] text-primary">primary</span>}
              </p>
              <p className="text-xs text-text-muted break-all">
                {[c.email ?? 'No email', c.title, c.phone].filter(Boolean).join(' · ')}
              </p>
            </div>
            {provisioned ? (
              <span className="text-xs text-text-muted">Access record exists — see Portal users for status</span>
            ) : canManage ? (
              <button
                type="button"
                onClick={() => onInvite(c)}
                disabled={!c.email}
                title={c.email ? undefined : 'This contact has no email in Zoho'}
                className="text-xs font-medium rounded-md border border-border bg-surface px-2.5 min-h-[44px] sm:min-h-[32px] text-text hover:bg-surface-strong disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Invite to portal
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function PortalUsersPanel({ users, onInvite, canManage }: { users: CustomerUserRow[]; onInvite: () => void; canManage: boolean }) {
  const styles: Record<CustomerUserRow['status'], string> = {
    invited: 'bg-warning-soft text-warning',
    active: 'bg-success-soft text-success',
    disabled: 'bg-surface-3 text-text-muted',
    revoked: 'bg-error-soft text-error',
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-xs text-text-muted">Logins linked to this account. Manage access for every customer on the Portal Access page.</p>
        {canManage && <button
          type="button"
          onClick={onInvite}
          className="text-xs font-medium rounded-md bg-primary text-text-inverse px-3 min-h-[44px] sm:min-h-[32px] hover:bg-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          Invite to portal
        </button>}
      </div>
      {users.length === 0 ? (
        <EmptyState
          icon={<svg className="w-10 h-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>}
          title="No portal users yet"
          subtitle="Nobody has been invited to the customer portal for this account."
        />
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {users.map((u) => (
            <li key={u.id} className="p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-text truncate">
                  {u.user.full_name ?? u.user.email}
                  {u.is_primary_contact && <span className="ml-2 rounded bg-primary-soft px-1.5 py-0.5 text-[10px] text-primary">primary</span>}
                </p>
                <p className="text-xs text-text-muted break-all">{u.user.email}</p>
                <p className="text-xs text-text-muted mt-0.5">
                  <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-medium mr-1.5 ${styles[u.status]}`}>{u.status}</span>
                  {u.role} · Last login {u.last_login_at ? formatDateTime(u.last_login_at) : 'never'}
                </p>
              </div>
              <a
                href={portalViewAsUrl(u.user_id)}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open the customer portal as ${u.user.email}`}
                className="inline-flex items-center justify-center text-xs font-medium rounded-md border border-border bg-surface px-2.5 min-h-[44px] sm:min-h-[32px] text-text hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              >
                Open portal as this user
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ─── Shared components ─── */

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-md border border-border bg-surface-2">
      <p className="text-[10px] font-semibold text-text-faint uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-text mt-0.5">{value}</p>
    </div>
  );
}

/* ─── Dialogs ─── */

function Dialog({ title, subtitle, onClose, children }: { title: string; subtitle?: string; onClose: () => void; children: ReactNode }) {
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const first = panelRef.current?.querySelector<HTMLElement>('input, select, textarea, button');
    first?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="dialog-title"
        className="bg-surface rounded-t-lg sm:rounded-lg shadow-xl border border-border p-5 sm:p-6 w-full sm:w-96 max-h-[90vh] overflow-y-auto pb-[calc(1.25rem+env(safe-area-inset-bottom))]"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="dialog-title" className="text-lg font-semibold text-text mb-1">{title}</h3>
        {subtitle && <p className="text-xs text-text-muted mb-4">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

const FIELD = 'w-full px-3 py-1.5 min-h-[44px] text-sm border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary';

function TicketDialog({
  customerId,
  customerName,
  locations,
  onClose,
  onCreated,
}: {
  customerId: string;
  customerName: string;
  locations: Array<Record<string, unknown>>;
  onClose: () => void;
  onCreated: () => void;
}) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState('medium');
  const [locationId, setLocationId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const submit = async () => {
    if (!title.trim()) return;
    setSubmitting(true);
    setErrorMessage(null);
    const { error } = await supabase.from('tickets').insert({
      customer_id: customerId,
      location_id: locationId || null,
      title: title.trim(),
      priority: priority as 'low' | 'medium' | 'high' | 'critical',
      source: 'internal',
      ticket_number: '',
      status: 'open',
    });
    setSubmitting(false);
    if (error) {
      setErrorMessage(`Could not create the ticket: ${error.message}`);
      return;
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['admin-customer-detail', customerId] }),
      queryClient.invalidateQueries({ queryKey: ['admin-tickets'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-locations'] }),
      queryClient.invalidateQueries({ queryKey: ['admin-customers-signals'] }),
    ]);
    onCreated();
    onClose();
  };

  return (
    <Dialog title="Create ticket" subtitle={`for ${customerName}`} onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-text-muted mb-1">Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} className={FIELD} />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-text-muted mb-1">Priority</span>
          <select value={priority} onChange={(e) => setPriority(e.target.value)} className={FIELD}>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-text-muted mb-1">Site</span>
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)} className={FIELD}>
            <option value="">No site</option>
            {locations.map((location) => (
              <option key={location.id as string} value={location.id as string}>
                {location.name as string}
              </option>
            ))}
          </select>
        </label>
        {errorMessage && <p role="alert" className="text-sm text-notification break-words">{errorMessage}</p>}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onClose} className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-border text-text-muted">Cancel</button>
        <button type="button" onClick={submit} disabled={submitting || !title.trim()} className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-primary text-text-inverse font-medium disabled:opacity-50">{submitting ? 'Creating…' : 'Create'}</button>
      </div>
    </Dialog>
  );
}

/**
 * Portal invite. Queues the `customer-provisioning-invite` Trigger.dev task;
 * the task creates the auth user and the customer_users link and sends the
 * magic link. Every invite is a `viewer` — customer contacts never receive a
 * staff-level role from this screen.
 */
function InviteDialog({
  customerId,
  customerName,
  prefill,
  onClose,
}: {
  customerId: string;
  customerName: string;
  prefill: InvitePrefill;
  onClose: () => void;
}) {
  const invite = useInviteCustomerUser();
  const { role } = useAuth();
  const [reason, setReason] = useState('');
  const [name, setName] = useState(prefill.full_name);
  const [email, setEmail] = useState(prefill.email);
  const [result, setResult] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const valid = canManageCustomerAccess(role) && email.trim().includes('@') && reason.trim().length >= 5;

  const submit = async () => {
    if (!valid) return;
    setErrorMessage(null);
    try {
      const res = await invite.mutateAsync({
        customer_id: customerId,
        email: email.trim(),
        reason,
        full_name: name.trim() || undefined,
        role: 'viewer',
        contact_id: prefill.contact_id,
        zoho_contact_id: prefill.zoho_contact_id,
        is_primary_contact: prefill.is_primary_contact ?? false,
      });
      setResult(res.runId ? `Invite queued (run ${res.runId}). The user appears under Portal users once the job runs.` : 'Invite queued. The user appears under Portal users once the job runs.');
    } catch (e) {
      setErrorMessage(describeJobError(e));
    }
  };

  return (
    <Dialog title="Invite to portal" subtitle={`for ${customerName}`} onClose={onClose}>
      <div className="space-y-3">
        <label className="block">
          <span className="block text-xs font-medium text-text-muted mb-1">Full name (optional)</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={FIELD} disabled={Boolean(result)} />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-text-muted mb-1">Email</span>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={FIELD} disabled={Boolean(result)} />
        </label>
        <label className="block">
          <span className="block text-xs font-medium text-text-muted mb-1">Reason for invitation (5–1000 characters)</span>
          <textarea value={reason} onChange={(event) => setReason(event.target.value)} maxLength={1000} rows={3} className={FIELD} disabled={Boolean(result)} />
        </label>
        <p className="text-xs text-text-muted">
          Role: <span className="font-medium text-text">viewer</span>. Portal users are read-only; staff roles are granted on the Roles page.
        </p>
        {result && <p role="status" className="text-sm text-success break-words">{result}</p>}
        {errorMessage && <p role="alert" className="text-sm text-notification break-words">{errorMessage}</p>}
      </div>
      <div className="flex justify-end gap-2 mt-5">
        <button type="button" onClick={onClose} className="px-3 py-1.5 min-h-[44px] text-sm rounded-md border border-border text-text-muted">{result ? 'Close' : 'Cancel'}</button>
        {!result && (
          <button type="button" onClick={submit} disabled={invite.isPending || !valid} className="px-3 py-1.5 min-h-[44px] text-sm rounded-md bg-primary text-text-inverse font-medium disabled:opacity-50">
            {invite.isPending ? 'Queuing…' : 'Send invite'}
          </button>
        )}
      </div>
    </Dialog>
  );
}

/**
 * Auvik tab — tenants currently mapped to this customer plus a quick
 * "link unmapped tenant" picker. The global mapping screen at
 * /integrations/auvik is the place for bulk assignment.
 */
function AuvikTab({ customerId }: { customerId: string }) {
  const { role } = useAuth();
  const canManage = role === 'admin' || role === 'superadmin';
  const { data: mapped, isLoading } = useAuvikTenantsForCustomer(customerId);
  const { data: allTenants } = useAuvikTenants();
  const { data: locations } = useLocationsForCustomer(customerId);
  const { data: live } = useCustomerNetworkLive(customerId);
  const map = useMapAuvikTenant();
  const unmap = useUnmapAuvikTenant();

  const unmapped = (allTenants ?? []).filter((t) => !t.customer_id);
  const [picking, setPicking] = useState<string | null>(null);
  const [pickLocation, setPickLocation] = useState<string>('');
  const [pickRevision, setPickRevision] = useState('');
  const [pickReason, setPickReason] = useState('');
  const [feedback, setFeedback] = useState<{ tone: 'success' | 'error'; message: string } | null>(null);

  const handleLink = async (tenantId: string) => {
    if (!canManage || !pickLocation || !pickRevision || pickReason.trim().length < 5) return;
    setFeedback(null);
    try {
      await map.mutateAsync({
        id: tenantId,
        customer_id: customerId,
        location_id: pickLocation,
        expected_updated_at: pickRevision,
        reason: pickReason.trim(),
      });
      setPicking(null);
      setPickLocation('');
      setPickReason('');
      setFeedback({ tone: 'success', message: 'Provider ownership saved. Review the monitoring registry if a consolidated collector needs to resume.' });
    } catch (e) {
      setFeedback({ tone: 'error', message: `Could not map this tenant: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const handleUnmap = async (id: string) => {
    const tenant = mapped?.find((row) => row.id === id);
    if (!canManage || !tenant) return;
    const reason = window.prompt(`Reason for detaching Auvik provider ${tenant.auvik_tenant_id} from this customer and location (at least 5 characters):`);
    if (!reason || reason.trim().length < 5) return;
    setFeedback(null);
    try {
      await unmap.mutateAsync({ id, expected_updated_at: tenant.updated_at, reason: reason.trim() });
      setFeedback({ tone: 'success', message: 'Tenant unmapped.' });
    } catch (e) {
      setFeedback({ tone: 'error', message: `Could not unmap this tenant: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const auvikConnected = (mapped?.length ?? 0) > 0;

  return (
    <div className="space-y-5">
      {feedback && (
        <p role={feedback.tone === 'error' ? 'alert' : 'status'} className={`text-sm break-words ${feedback.tone === 'error' ? 'text-error' : 'text-success'}`}>
          {feedback.message}
        </p>
      )}
      <div>
        <h3 className="text-sm font-semibold text-text">Mapped Auvik tenants</h3>
        <p className="text-xs text-text-muted">
          Customers can have multiple Auvik tenants — one per physical site.
        </p>
        {isLoading ? (
          <div className="mt-3 text-sm text-text-muted">Loading…</div>
        ) : mapped && mapped.length > 0 ? (
          <ul className="mt-2 divide-y divide-border border border-border rounded-md">
            {mapped.map((t) => (
              <li key={t.id} className="px-3 py-2 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-text font-medium truncate">
                    {t.auvik_tenant_name ?? 'Auvik tenant'}
                  </div>
                  <CopyId value={t.auvik_tenant_id} />
                </div>
                {canManage && <button
                  type="button"
                  onClick={() => handleUnmap(t.id)}
                  disabled={unmap.isPending}
                  className="text-xs text-error hover:underline min-h-[44px] sm:min-h-0 disabled:opacity-50"
                >
                  Unmap
                </button>}
              </li>
            ))}
          </ul>
        ) : (
          <div className="mt-2 text-sm text-text-muted border border-dashed border-border rounded-md p-4 text-center">
            Auvik not connected: no tenant is mapped to this customer.
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-semibold text-text">Link an unmapped tenant</h3>
        <p className="text-xs text-text-muted">
          Pick a tenant from the Auvik inventory that is not assigned to any customer yet.
        </p>
        {unmapped.length === 0 ? (
          <div className="mt-2 text-sm text-text-muted border border-dashed border-border rounded-md p-4 text-center">
            {(allTenants?.length ?? 0) === 0
              ? 'The Auvik tenant inventory is empty. Sync it from '
              : 'Every tenant in the inventory is already mapped. Re-assign one from '}
            <Link to="/integrations/auvik" className="text-primary hover:underline">
              the Auvik mapping page
            </Link>
            .
          </div>
        ) : (
          <ul className="mt-2 divide-y divide-border border border-border rounded-md max-h-96 overflow-y-auto">
            {unmapped.map((t) => (
              <li key={t.id} className="px-3 py-2">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm text-text font-medium truncate">
                      {t.auvik_tenant_name ?? 'Auvik tenant'}
                    </div>
                    <CopyId value={t.auvik_tenant_id} />
                  </div>
                  {canManage && (picking === t.id ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="sr-only" htmlFor={`pick-location-${t.id}`}>Exact location</label>
                      <select
                        id={`pick-location-${t.id}`}
                        value={pickLocation}
                        onChange={(e) => setPickLocation(e.target.value)}
                        className="rounded border border-border bg-surface px-2 py-1 min-h-[40px] text-xs text-text"
                      >
                        <option value="">— Select exact location —</option>
                        {(locations ?? []).map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.name} ({l.id})
                          </option>
                        ))}
                      </select>
                      <label className="sr-only" htmlFor={`pick-reason-${t.id}`}>Reason for provider ownership change</label>
                      <input id={`pick-reason-${t.id}`} value={pickReason} onChange={(e) => setPickReason(e.target.value)} maxLength={1000} placeholder="Reason for ownership change" className="rounded border border-border bg-surface px-2 py-1 min-h-[40px] text-xs text-text" />
                      <button
                        type="button"
                        onClick={() => handleLink(t.id)}
                        disabled={map.isPending || !pickLocation || pickReason.trim().length < 5}
                        className="rounded bg-primary hover:bg-primary-hover disabled:opacity-50 px-2 py-1 min-h-[40px] text-xs font-medium text-text-inverse"
                      >
                        {map.isPending ? 'Linking…' : 'Link'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setPicking(null);
                          setPickLocation('');
                        }}
                        className="text-xs text-text-muted hover:text-text min-h-[40px]"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setPicking(t.id); setPickRevision(t.updated_at); setPickReason(''); }}
                      className="text-xs text-primary hover:underline min-h-[44px] sm:min-h-0 text-left"
                    >
                      Link to this customer
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <LiveList
        title="Devices"
        source="Auvik"
        href="/command-center"
        empty={auvikConnected ? 'No Auvik devices synced for this customer yet.' : 'Auvik not connected — map a tenant to receive device inventory.'}
        rows={(live?.devices ?? []).map((d) => ({
          id: d.id,
          primary: d.name ?? 'Device',
          secondary: [
            d.device_type,
            d.vendor_name,
            d.make_model,
            d.ip_address,
            d.availability_pct == null ? null : formatAvailabilityPct(d.availability_pct),
          ].filter(Boolean).join(' · '),
          badge: d.status,
        }))}
      />
      <LiveList
        title="Open alerts"
        source="Auvik"
        href="/command-center"
        empty={auvikConnected ? 'No open Auvik alerts.' : 'Auvik not connected.'}
        rows={(live?.alerts ?? []).map((a) => ({
          id: a.id,
          primary: a.alert_name ?? a.entity_name ?? 'Alert',
          secondary: [relName(a.devices), relName(a.locations), a.message].filter(Boolean).join(' · '),
          badge: a.severity,
          href: '/command-center',
        }))}
      />
      <LiveList
        title="Interfaces"
        source="Auvik"
        empty={auvikConnected ? 'No interfaces synced yet.' : 'Auvik not connected.'}
        rows={(live?.interfaces ?? []).slice(0, 40).map((i) => ({
          id: i.id,
          primary: i.name ?? 'Interface',
          secondary: [relName(i.devices), i.interface_type, i.ip_address, i.negotiated_speed].filter(Boolean).join(' · '),
          badge: i.operational_status,
        }))}
      />
      <LiveList
        title="UniFi sites"
        source="UniFi Site Manager"
        href="/integrations"
        empty="No UniFi site is mapped to this customer. Map sites on the Integrations page."
        rows={(live?.unifiSites ?? []).map((s) => ({
          id: s.id,
          primary: s.site_name ?? s.unifi_host_name ?? 'Site',
          secondary: [
            relName(s.locations),
            s.wifi_client_count === null ? null : `${s.wifi_client_count} Wi-Fi clients`,
            s.wired_client_count === null ? null : `${s.wired_client_count} wired clients`,
            s.ssid_count === null ? null : `${s.ssid_count} SSIDs`,
          ].filter(Boolean).join(' · '),
        }))}
      />
      <LiveList
        title="SSIDs"
        source="UniFi"
        empty="No UniFi SSIDs stored for this customer."
        rows={(live?.wlans ?? []).map((w) => ({
          id: w.id,
          primary: w.name ?? 'SSID',
          secondary: [relName(w.locations), w.security, w.enabled === false ? 'disabled' : w.enabled === true ? 'enabled' : null].filter(Boolean).join(' · '),
        }))}
      />
      <LiveList
        title="Connected clients"
        source="UniFi"
        empty="No UniFi clients stored for this customer."
        rows={(live?.clients ?? []).map((c) => ({
          id: c.id,
          primary: c.name ?? c.mac_address ?? 'Client',
          secondary: [relName(c.locations), c.ssid, c.connection_type, c.ip_address].filter(Boolean).join(' · '),
        }))}
      />
      <LiveList
        title="Access points"
        source="UniFi"
        empty="No UniFi access points stored for this customer."
        rows={(live?.accessPoints ?? []).map((ap) => ({
          id: ap.id,
          primary: ap.name ?? ap.model ?? 'Access point',
          secondary: [relName(ap.locations), ap.model, ap.mac_address, ap.ip_address].filter(Boolean).join(' · '),
        }))}
      />
    </div>
  );
}

function relName(rel: { name: string | null } | { name: string | null }[] | null | undefined): string | null {
  if (!rel) return null;
  const row = Array.isArray(rel) ? rel[0] : rel;
  return row?.name ?? null;
}

function LiveList({
  title,
  source,
  empty,
  href,
  rows,
}: {
  title: string;
  source: string;
  empty: string;
  href?: string;
  rows: Array<{ id: string; primary: string; secondary?: string; badge?: string | null; href?: string }>;
}) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold text-text">{title}</h3>
        <p className="text-xs text-text-muted">
          {source}
          {href && (
            <>
              {' · '}
              <Link to={href} className="text-primary hover:underline">Open</Link>
            </>
          )}
        </p>
      </div>
      {rows.length === 0 ? (
        <p className="mt-2 text-sm text-text-muted border border-dashed border-border rounded-md p-4 text-center">{empty}</p>
      ) : (
        <ul className="mt-2 divide-y divide-border border border-border rounded-md">
          {rows.map((row) => {
            const body = (
              <>
                <div className="min-w-0">
                  <p className="text-sm text-text truncate">{row.primary}</p>
                  {row.secondary && <p className="text-xs text-text-muted truncate">{row.secondary}</p>}
                </div>
                {row.badge && <StatusBadge status={row.badge} />}
              </>
            );
            return row.href ? (
              <li key={row.id}>
                <Link to={row.href} className="px-3 py-2 flex items-start justify-between gap-3 hover:bg-surface-strong">
                  {body}
                </Link>
              </li>
            ) : (
              <li key={row.id} className="px-3 py-2 flex items-start justify-between gap-3">
                {body}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function GenerateSlaReportButton({ customerId }: { customerId: string }) {
  const [state, setState] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  async function trigger() {
    setState('running');
    setMessage(null);
    const lastMonth = new Date();
    lastMonth.setUTCMonth(lastMonth.getUTCMonth() - 1);
    const month = lastMonth.toISOString().slice(0, 7);
    try {
      const result = await invokeTriggerTask(
        'sla-report-generate',
        { customerId, month },
        { idempotencyKey: `sla-${customerId}-${month}` },
      );
      setState('done');
      setMessage(`Queued for ${month}${result.runId ? ` (run ${result.runId})` : ''}. The PDF appears in the customer's Documents once rendered.`);
    } catch (e) {
      setMessage(describeJobError(e));
      setState('error');
    }
  }

  return (
    <div className="space-y-1">
      <ActionBtn
        label={state === 'running' ? 'Queuing SLA report…' : 'Generate SLA report'}
        onClick={trigger}
        disabled={state === 'running'}
      />
      {state === 'done' && message && (
        <p role="status" className="text-[11px] text-success break-words">{message}</p>
      )}
      {state === 'error' && message && (
        <p role="alert" className="text-[11px] text-notification break-words">{message}</p>
      )}
    </div>
  );
}
